
export type OddsFormat = 'american' | 'decimal' | 'fractional' | 'cents';

const TOOL_ODDS_FORMATS = ['american', 'decimal', 'cents'] as const satisfies readonly OddsFormat[];

export type ToolOddsFormat = (typeof TOOL_ODDS_FORMATS)[number];

export function isToolOddsFormat(value: unknown): value is ToolOddsFormat {
  return TOOL_ODDS_FORMATS.some((format) => format === value);
}

export function americanToDecimal(americanOdds: number): number {
  if (americanOdds > 0) {
    return (americanOdds / 100) + 1;
  } else {
    return (100 / Math.abs(americanOdds)) + 1;
  }
}

export function fractionalToDecimal(fractionalOdds: string): number {
  const [numerator, denominator] = fractionalOdds.split('/').map(Number);
  return (numerator / denominator) + 1;
}

export function centsToDecimal(centsOdds: number): number {
  if (!Number.isFinite(centsOdds) || centsOdds <= 0) {
    return NaN;
  }
  return 100 / centsOdds;
}

export function decimalToAmerican(decimalOdds: number): number {
  if (decimalOdds >= 2.0) {
    return Math.round((decimalOdds - 1) * 100);
  } else {
    return Math.round(-100 / (decimalOdds - 1));
  }
}

export function decimalToFractional(decimalOdds: number): string {
  const decimal = decimalOdds - 1;
  
  const gcd = (a: number, b: number): number => {
    return b ? gcd(b, a % b) : a;
  };
  
  const precision = 100;
  const numerator = Math.round(decimal * precision);
  const denominator = precision;
  
  const divisor = gcd(numerator, denominator);
  const simplifiedNum = numerator / divisor;
  const simplifiedDenom = denominator / divisor;
  
  return `${simplifiedNum}/${simplifiedDenom}`;
}

export function convertOdds(
  odds: number | string, 
  fromFormat: OddsFormat, 
  toFormat: OddsFormat
): number | string {
  let decimalOdds: number;
  
  if (fromFormat === 'american') {
    decimalOdds = americanToDecimal(odds as number);
  } else if (fromFormat === 'fractional') {
    decimalOdds = fractionalToDecimal(odds as string);
  } else if (fromFormat === 'cents') {
    decimalOdds = centsToDecimal(odds as number);
  } else {
    decimalOdds = odds as number;
  }
  
  if (toFormat === 'american') {
    return decimalToAmerican(decimalOdds);
  } else if (toFormat === 'fractional') {
    return decimalToFractional(decimalOdds);
  } else if (toFormat === 'cents') {
    return decimalToCents(decimalOdds);
  } else {
    return decimalOdds;
  }
}

export function decimalToImpliedProbability(decimalOdds: number): number {
  return 1 / decimalOdds;
}

export function decimalToCents(decimalOdds: number): number {
  return decimalToImpliedProbability(decimalOdds) * 100;
}

export function formatCentsOdds(decimalOdds: number): string {
  if (!Number.isFinite(decimalOdds) || decimalOdds <= 1) {
    return '';
  }
  const cents = decimalToCents(decimalOdds);
  const rounded = Math.round(cents * 10) / 10;
  const display = Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1).replace(/\.0$/, "");
  return `${display}c`;
}

export function formatProbability(probability: number): string {
  return (probability * 100).toFixed(2) + '%';
}

export function formatAmericanOdds(americanOdds: number): string {
  return americanOdds > 0 ? `+${americanOdds}` : `${americanOdds}`;
}

export function formatDecimalOdds(decimalOdds: number, toFormat: OddsFormat): string {
  if (!Number.isFinite(decimalOdds) || decimalOdds <= 1) {
    return '';
  }

  if (toFormat === 'decimal') {
    return decimalOdds.toFixed(2);
  }

  if (toFormat === 'cents') {
    return formatCentsOdds(decimalOdds);
  }

  if (toFormat === 'american') {
    const american = convertOdds(decimalOdds, 'decimal', 'american') as number;
    return formatAmericanOdds(american);
  }

  const fractional = convertOdds(decimalOdds, 'decimal', 'fractional') as string;
  return String(fractional);
}

export function isFractionalFormat(odds: string): boolean {
  return /^\d+\/\d+$/.test(odds);
}

export function parseOddsInput(
  input: string,
  preferredFormat?: OddsFormat
): { value: number | string; format: OddsFormat } {
  const trimmed = String(input ?? '').trim();

  if (!trimmed) {
    return { value: 0, format: preferredFormat ?? 'decimal' };
  }

  if (isFractionalFormat(trimmed)) {
    return { value: trimmed, format: 'fractional' };
  }

  if (trimmed.startsWith('+') || trimmed.startsWith('-')) {
    return { value: parseInt(trimmed, 10), format: 'american' };
  }

  const num = parseFloat(trimmed);
  if (!isNaN(num)) {
    if (preferredFormat === 'decimal') {
      return { value: num, format: 'decimal' };
    }

    if (preferredFormat === 'cents') {
      return { value: num, format: 'cents' };
    }

    if (num > 0 && num < 10) {
      return { value: num, format: 'decimal' };
    } else {
      return { value: num, format: 'american' };
    }
  }
  
  return { value: 0, format: preferredFormat ?? 'decimal' };
}

export function parseOddsToDecimal(
  input: string,
  preferredFormat?: OddsFormat
): { decimal: number | null; detectedFormat: OddsFormat } {
  const { value, format } = parseOddsInput(String(input ?? '').trim(), preferredFormat);

  try {
    const decimal = convertOdds(value as any, format, 'decimal') as number;
    if (!Number.isFinite(decimal) || decimal <= 1) {
      return { decimal: null, detectedFormat: format };
    }
    return { decimal, detectedFormat: format };
  } catch {
    return { decimal: null, detectedFormat: format };
  }
}

export function normalizeOddsInput(
  input: string,
  targetFormat: OddsFormat,
  sourceFormat?: OddsFormat
): { displayValue: string; decimalValue: number | null; detectedFormat: OddsFormat } {
  const raw = String(input ?? '').trim();
  const { decimal, detectedFormat } = parseOddsToDecimal(raw, sourceFormat);

  if (decimal === null) {
    return { displayValue: raw, decimalValue: null, detectedFormat };
  }

  const displayValue = formatDecimalOdds(decimal, targetFormat);
  return { displayValue, decimalValue: decimal, detectedFormat };
}
